import { useEffect, useRef, useState, type RefObject } from 'react';

export type Scale = ((v: number) => number) & { invert: (px: number) => number; domain: [number, number] };

export function linearScale(d0: number, d1: number, r0: number, r1: number): Scale {
  const span = d1 - d0 || 1;
  const f = ((v: number) => r0 + ((v - d0) / span) * (r1 - r0)) as Scale;
  f.invert = (px: number) => d0 + ((px - r0) / (r1 - r0 || 1)) * span;
  f.domain = [d0, d1];
  return f;
}

export function logScale(d0: number, d1: number, r0: number, r1: number): Scale {
  const l0 = Math.log10(Math.max(d0, 1e-300));
  const l1 = Math.log10(Math.max(d1, 1e-300));
  const inner = linearScale(l0, l1, r0, r1);
  const f = ((v: number) => inner(Math.log10(Math.max(v, 1e-300)))) as Scale;
  f.invert = (px: number) => 10 ** inner.invert(px);
  f.domain = [d0, d1];
  return f;
}

/** "Nice" round ticks covering [min, max]. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-9; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

export function niceDomain(min: number, max: number, count = 4): [number, number] {
  const ticks = niceTicks(min, max, count);
  if (ticks.length < 2) return [min, max];
  const step = ticks[1] - ticks[0];
  return [Math.floor(min / step) * step, Math.ceil(max / step) * step];
}

/** Powers-of-ten-ish ticks for a log axis (1, 2, 5 × 10^n). */
export function logTicks(min: number, max: number, count = 4): number[] {
  const out: number[] = [];
  const e0 = Math.floor(Math.log10(min));
  const e1 = Math.ceil(Math.log10(max));
  for (let e = e0; e <= e1; e++) for (const m of [1, 2, 5]) out.push(m * 10 ** e);
  const inRange = out.filter((v) => v >= min && v <= max);
  if (inRange.length <= count + 1) return inRange;
  const stride = Math.ceil(inRange.length / (count + 1));
  return inRange.filter((_, i) => i % stride === 0);
}

const HOUR = 3_600_000;
const STEPS = [15 * 60_000, 30 * 60_000, HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, 24 * HOUR, 2 * 24 * HOUR, 7 * 24 * HOUR];

export function timeTicks(t0: number, t1: number, count = 5): { ticks: number[]; step: number } {
  const span = t1 - t0;
  const step = STEPS.find((s) => span / s <= count) ?? STEPS[STEPS.length - 1];
  const ticks: number[] = [];
  for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) ticks.push(t);
  return { ticks, step };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtTime(t: number, step: number): string {
  const d = new Date(t);
  if (step >= 24 * HOUR || (d.getUTCHours() === 0 && d.getUTCMinutes() === 0)) return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

export function fmtDateTime(t: number): string {
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} UTC`;
}

/** Index of the item whose `t` is nearest to `t`. Items must be sorted by t. */
export function nearestIndex<T extends { t: number }>(items: T[], t: number): number {
  let lo = 0;
  let hi = items.length - 1;
  if (hi < 0) return -1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(items[lo - 1].t - t) < Math.abs(items[lo].t - t)) return lo - 1;
  return lo;
}

/** Tracks an element's content-box width. */
export function useWidth<T extends HTMLElement>(fallback = 600): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth || fallback);
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width];
}

export function linePath(points: [number, number][]): string {
  if (!points.length) return '';
  return points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join('');
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}
